import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";

import { RegistryEditor } from "./RegistryEditor";
import { listRegistry, messageFor, REGISTRY_QUERY_KEY, type RegistryRow } from "./api";
import { fmtMin, parisDateTime, storeLabel } from "./format";

const PAGE_SIZE = 50;

export function SettingsTab({ areaName }: { areaName: string }) {
  const client = useQueryClient();
  const [pageIndex, setPageIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const query = useQuery({ queryKey: REGISTRY_QUERY_KEY, queryFn: listRegistry });
  const rows = query.data?.data ?? [];
  const lastPage = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1);
  const page = Math.min(pageIndex, lastPage);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // "Changed": the row's content version, served by C2 v1.4 (D10 closed); a reseed touch does not move it.
  const columns: ColumnDef<RegistryRow, unknown>[] = [
    {
      id: "sku", header: "SKU",
      cell: ({ row }) => (
        <button type="button" data-nonmutating className="text-left text-ks-ink underline-offset-2 hover:underline"
          onClick={() => setSelected(`${row.original.sku}|${row.original.channel_location}`)}>
          <span className="font-medium">{row.original.sku}</span>
          <span className="block text-xs text-muted-foreground">{row.original.name}</span>
        </button>
      ),
    },
    { id: "store", header: "Store", cell: ({ row }) => storeLabel(row.original) },
    { id: "kind", header: "Kind", cell: ({ row }) => row.original.row_kind },
    {
      id: "min", header: "Min days",
      cell: ({ row }) => {
        const m = fmtMin(row.original.effective_min_days);
        if (m == null) return "No minimum";
        return row.original.min_days != null ? `${m} (override)` : m;
      },
    },
    { id: "status", header: "Status", cell: ({ row }) => row.original.status },
    { id: "changed", header: "Changed", cell: ({ row }) => parisDateTime(row.original.updated_at) ?? "" },
  ];

  const selectedRow = rows.find((r) => `${r.sku}|${r.channel_location}` === selected) ?? null;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_28rem]">
      <div>
        <p className="mb-2 text-sm text-ks-ink">Changes apply on the next run. Every change is recorded with who made it and why.</p>
        {query.isError ? (
          <p role="alert" className="text-sm text-ks-danger">{messageFor(query.error, areaName)}</p>
        ) : (
          <DataTable
            columns={columns}
            rows={pageRows}
            page={{ pageIndex: page, pageSize: PAGE_SIZE, rowCount: rows.length }}
            onPageChange={setPageIndex}
            pending={query.isLoading}
            emptyMessage="No registry rows yet — run the seed first."
          />
        )}
      </div>
      {selectedRow ? (
        <RegistryEditor
          key={selected ?? ""}
          row={selectedRow}
          areaName={areaName}
          onSaved={(next) => {
            client.setQueryData(REGISTRY_QUERY_KEY, (prev: { data: RegistryRow[]; meta: unknown } | undefined) =>
              prev ? { ...prev, data: prev.data.map((r) => (r.sku === next.sku && r.channel_location === next.channel_location ? next : r)) } : prev,
            );
          }}
        />
      ) : (
        <p className="text-sm text-muted-foreground">Select a row to change its minimum days of stock or retire it.</p>
      )}
    </div>
  );
}
